import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOBreadcrumbs from '../components/SEOBreadcrumbs';
import AccordionFAQ from '../components/AccordionFAQ';
import { getFaqDataStatic } from '../data/faqDataI18n';
import { useTranslation } from '../hooks/useTranslation';
import { usePageSEO } from '../hooks/usePageSEO';
import { 
  Email, 
  SupportAgent, 
  Schedule, 
  HelpOutline,
  AccountCircle,
  TrendingUp,
  WaterDrop,
  Flag,
  Restaurant,
  FitnessCenter,
  SmartToy,
  Notifications,
  CreditCard,
  Shield,
  Settings,
  CalendarMonth,
  Favorite,
  LocalPharmacy,
  Warning,
  ExpandMore,
  ExpandLess
} from '../mui-icons';
import './SupportPage.css';

// Icon map
const iconMap = {
  'account-circle': AccountCircle,
  'chart-line': TrendingUp,
  'water': WaterDrop,
  'flag': Flag,
  'food': Restaurant,
  'dumbbell': FitnessCenter,
  'calendar-clock': CalendarMonth,
  'calendar-heart': Favorite,
  'medical-bag': LocalPharmacy,
  'alert-circle': Warning,
  'robot': SmartToy,
  'bell': Notifications,
  'credit-card': CreditCard,
  'shield-check': Shield,
  'cog': Settings,
  'help-circle': HelpOutline
};

const SupportPage = () => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState('contact');
  const [activeSection, setActiveSection] = useState('contact');
  const sectionRefs = useRef({});
  
  // SEO dinâmico para esta página
  usePageSEO();
  
  // Obter dados do FAQ traduzidos
  const faqData = getFaqDataStatic(t);
  
  // Gerar FAQ Schema JSON-LD para SEO
  const faqSchemaItems = faqData.flatMap(category => 
    category.questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  ).slice(0, 10); // Limitar a 10 perguntas principais para o schema
  
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSchemaItems
  };

  // Criar sections array (1 para contato + 16 para FAQ + 1 para CTA final)
  const sections = [
    { id: 'contact', icon: <Email />, color: '#4CAF50', categoryKey: null },
    ...faqData.map((category) => ({
      id: category.categoryKey,
      icon: iconMap[category.icon] ? React.createElement(iconMap[category.icon]) : <HelpOutline />,
      color: getCategoryColor(category.categoryKey),
      categoryKey: category.categoryKey
    })),
    { id: 'help', icon: <SupportAgent />, color: '#FF9800', categoryKey: null }
  ];

  // Função para obter cor por categoria
  function getCategoryColor(categoryKey) {
    const colors = {
      'account': '#2196F3',
      'progress': '#4CAF50',
      'water': '#03A9F4',
      'goals': '#FF9800',
      'meals': '#FF6B6B',
      'activities': '#9C27B0',
      'routines': '#607D8B',
      'cycle': '#E91E63',
      'medications': '#3F51B5',
      'allergies': '#FF5722',
      'ai': '#00BCD4',
      'notifications': '#795548',
      'subscription': '#FFB74D',
      'privacy': '#8BC34A',
      'technical': '#F44336',
      'general': '#00897B'
    };
    return colors[categoryKey] || '#757575';
  }

  // Scroll to section and expand
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSection(sectionId);
      setActiveSection(sectionId);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -80% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleContact = () => {
    window.location.href = `mailto:contato@personalnutri.app?subject=${t('support.contact.subject')}`;
  };

  // Render Contact section
  const renderContactSection = () => (
    <div className="section-content">
      <div className="contact-info-main">
        <p className="contact-text-main">{t('support.contact.text')}</p>
        <div className="contact-items-main">
          <div className="contact-item-main">
            <Email className="contact-icon-main" />
            <div className="contact-info-detail">
              <strong>{t('support.contact.email')}</strong>
              <a href={`mailto:contato@personalnutri.app?subject=${t('support.contact.subject')}`} className="email-link-main">
                {t('support.contact.emailAddress')}
              </a>
            </div>
          </div>
          <div className="contact-item-main">
            <Schedule className="contact-icon-main" />
            <div className="contact-info-detail">
              <strong>{t('support.contact.schedule')}</strong>
              <span>{t('support.contact.scheduleText')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render FAQ section
  const renderFaqSection = (categoryKey) => {
    const category = faqData.find(cat => cat.categoryKey === categoryKey);
    if (!category) return null;

    return (
      <div className="section-content">
        <div className="faq-category-info">
          <span className="category-badge-main">{category.questions.length} {category.questions.length === 1 ? 'pergunta' : 'perguntas'}</span>
        </div>
        <div className="faq-questions-main">
          {category.questions.map((item, index) => (
            <AccordionFAQ 
              key={index}
              question={item.question}
              answer={item.answer}
            />
          ))}
        </div>
      </div>
    );
  };

  // Render Help CTA section
  const renderHelpSection = () => (
    <div className="help-cta-main">
      <SupportAgent sx={{ fontSize: 60, color: 'white' }} />
      <h3 className="help-cta-title">{t('support.cta.title')}</h3>
      <p className="help-cta-text">{t('support.cta.text')}</p>
      <button onClick={handleContact} className="contact-button-main">
        <Email />
        {t('support.cta.button')}
      </button>
    </div>
  );

  // Section renderers mapping
  const sectionRenderers = {
    contact: renderContactSection,
    help: renderHelpSection
  };

  // Add FAQ section renderers
  faqData.forEach((category) => {
    sectionRenderers[category.categoryKey] = () => renderFaqSection(category.categoryKey);
  });

  // Get section title
  const getSectionTitle = (sectionId) => {
    if (sectionId === 'contact') return t('support.contact.title');
    if (sectionId === 'help') return t('support.cta.title');
    
    const category = faqData.find(cat => cat.categoryKey === sectionId);
    return category ? category.title : sectionId;
  };

  return (
    <div className="support-page surface-marketing">
      <Header />
      
      {/* SEO Breadcrumbs */}
      <SEOBreadcrumbs />
      
      {/* FAQ Schema JSON-LD para rich snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="support-hero">
        <div className="container">
          <div className="support-hero-content">
            <SupportAgent sx={{ fontSize: 60 }} />
            <h1 className="support-hero-title">{t('support.hero.title')}</h1>
            <p className="support-hero-subtitle">{t('support.hero.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="support-layout">
        <div className="container support-container">
          {/* Sidebar Navigation */}
          <aside className="support-sidebar">
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                  style={{ '--section-color': section.color }}
                >
                  <div className="sidebar-icon" style={{ color: section.color }}>
                    {section.icon}
                  </div>
                  <span className="sidebar-label">{getSectionTitle(section.id)}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content Area */}
          <main className="support-content">
            {sections.map((section) => {
              // Help CTA não é expansível
              const isNonExpandable = section.id === 'help';
              
              if (isNonExpandable) {
                return (
                  <div
                    key={section.id}
                    ref={(el) => (sectionRefs.current[section.id] = el)}
                    data-section={section.id}
                    className="section-wrapper"
                  >
                    <div className="section-card pn-card pn-card--marketing pn-card--marketing-expandable-static">
                      {renderHelpSection()}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={section.id}
                  ref={(el) => (sectionRefs.current[section.id] = el)}
                  data-section={section.id}
                  className="section-wrapper"
                >
                  <div
                    className={`section-card ${expandedSection === section.id ? 'expanded' : ''}`}
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="section-header">
                      <div className="section-header-left">
                        <div className="section-icon" style={{ backgroundColor: section.color }}>
                          {section.icon}
                        </div>
                        <h2 className="section-title">{getSectionTitle(section.id)}</h2>
                      </div>
                      <div className="section-toggle">
                        {expandedSection === section.id ? (
                          <ExpandLess sx={{ fontSize: 32 }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 32 }} />
                        )}
                      </div>
                    </div>

                    {expandedSection === section.id && (
                      <div className="section-body" onClick={(e) => e.stopPropagation()}>
                        {sectionRenderers[section.id]()}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SupportPage;
